import { defineStore } from "pinia";

import { getCompanyId } from "@/utils/userUtils";
import { exportToExcel } from "@/utils/fileUtils";
import { useTableConfigStore } from "@/stores/tableConfigStore";

import tripsApi from "@/services/api/tripsApi";

export const useTripStore = defineStore("trip", {
  state: () => ({
    trips: [],

    companyId: getCompanyId(),

    dataIsLoading: false,

    lastFetchParams: {},

    pagination: {
      page: 1,
      pages: 1,
      series: [],
      next: null,
      prev: null,
      count: 0,
      limit: 40,
      from: 0,
      to: 0,
    },

    tripStatusOptions: [
      { value: "planned", label: "Запланирован", color: "#ca8a04" },
      { value: "in_progress", label: "В пути", color: "#4f46e5" },
      { value: "completed", label: "Завершён", color: "#16a34a" },
      {
        value: "canceled",
        label: "Отменён",
        color: "var(--color-danger)",
      },
      { value: "draft", label: "Черновик", color: "#6b7280" },
    ],

    pointTypeOptions: [
      { value: "loading", label: "Погрузка" },
      { value: "unloading", label: "Разгрузка" },
      { value: "loading_unloading", label: "Погрузка/Разгрузка" },
      { value: "transit", label: "Без погрузки/разгрузки" },
    ],

    actionTypeOptions: [
      { value: "arrived", label: "Прибыл" },
      { value: "loaded", label: "Погрузка" },
      { value: "unloaded", label: "Разгрузка" },
      { value: "left_point", label: "Погрузка/Разгрузка" },
    ],

    loadingMethodOptions: [
      { value: "top", label: "Сверху" },
      { value: "rear", label: "Сзади" },
      { value: "side", label: "Сбоку" },
      { value: "full_tenting", label: "Полная растентовка" },
    ],

    activeTripsOptions: [
      { value: "true", label: "Активные" },
      { value: "false", label: "Неактивные" },
    ],
  }),

  getters: {
    tripStatusLabels: (state) =>
      Object.fromEntries(
        state.tripStatusOptions.map((opt) => [opt.value, opt.label])
      ),

    // Опции для BaseStatusSelect: статус рейса с цветом
    tripStatusSelectOptions() {
      return this.tripStatusOptions.map((opt) => ({
        value: opt.value,
        label: opt.label,
        color: opt.color,
      }));
    },

    pointTypeLabels: (state) =>
      Object.fromEntries(
        state.pointTypeOptions.map((opt) => [opt.value, opt.label])
      ),

    actionTypeLabels: (state) =>
      Object.fromEntries(
        state.actionTypeOptions.map((opt) => [opt.value, opt.label])
      ),

    loadingMethodLabels: (state) =>
      Object.fromEntries(
        state.loadingMethodOptions.map((opt) => [
          opt.value,
          opt.label,
        ])
      ),
  },

  actions: {
    // Обновляет статус рейса без перезагрузки всей формы
    async updateStatus(tripId, status) {
      await tripsApi.update(this.companyId, tripId, { status });
    },

    formatTripStatus(status) {
      return this.tripStatusLabels[status] || "Другое";
    },

    formatPointType(type) {
      return this.pointTypeLabels[type] || "Другое";
    },

    formatActionType(type) {
      return this.actionTypeLabels[type] || "Другое";
    },

    formatLoadingMethod(method) {
      if (!method) return "—";
      return this.loadingMethodLabels[method] || "Другое";
    },

    tripStatusColor(status) {
      return (
        this.tripStatusOptions.find((option) => option.value === status)
          ?.color || "#6b7280"
      );
    },

    tripStatusBorderColor(status) {
      switch (status) {
        case "completed":
          return "5px solid #16a34a";
        case "in_progress":
          return "5px solid #4f46e5";
        case "canceled":
          return "5px solid var(--color-danger)";
        default:
          return "5px solid #6b7280";
      }
    },

    async fetchTrips(params = {}, columnsConfig = []) {
      try {
        this.dataIsLoading = true;

        this.lastFetchParams = params;

        // Страница задаёт enabled сама: отсутствие фильтра включает неактивные рейсы.
        const response = await tripsApi.index(
          this.companyId,
          params
        );
        let rawTrips = response.data.result;

        if (columnsConfig.length > 0) {
          const tableConfigStore = useTableConfigStore();
          this.trips = tableConfigStore.enrichRecordsWithCustomFields(
            rawTrips,
            columnsConfig
          );
        } else {
          this.trips = rawTrips;
        }

        const meta = response.data.pagination_meta;
        this.pagination = {
          page: meta.page,
          pages: meta.pages,
          series: meta.series,
          next: meta.next,
          prev: meta.prev,
          count: meta.count,
          limit: meta.limit,
          from: meta.from,
          to: meta.to,
        };
      } catch (error) {
        console.error("Ошибка при загрузке рейсов:", error);
        this.trips = [];
      } finally {
        this.dataIsLoading = false;
      }
    },

    async loadMoreTrips() {
      if (
        this.dataIsLoading ||
        this.pagination.page >= this.pagination.pages
      )
        return;

      try {
        this.dataIsLoading = true;

        const requestParams = {
          ...this.lastFetchParams,
          page: this.pagination.page + 1,
        };

        const response = await tripsApi.index(
          this.companyId,
          requestParams
        );

        this.trips = [...this.trips, ...response.data.result];

        const meta = response.data.pagination_meta;
        this.pagination = {
          page: meta.page,
          pages: meta.pages,
          series: meta.series,
          next: meta.next,
          prev: meta.prev,
          count: meta.count,
          limit: meta.limit,
          from: meta.from,
          to: meta.to,
        };
      } catch (error) {
        console.error("Ошибка при подгрузке рейсов:", error);
      } finally {
        this.dataIsLoading = false;
      }
    },

    async exportTrips(params) {
      try {
        const response = await tripsApi.export(
          this.companyId,
          params
        );
        exportToExcel(response, "trips.xlsx");
      } catch (error) {
        console.error("Ошибка при экспорте:", error);
        throw error;
      }
    },

    async enableTrips(tripsList) {
      try {
        const promises = tripsList.map((trip) =>
          tripsApi.enable(this.companyId, trip.id)
        );
        await Promise.all(promises);
      } catch (error) {
        console.error("Ошибка при включении рейсов:", error);
        throw error;
      }
    },

    async disableTrips(tripsList) {
      try {
        const promises = tripsList.map((trip) =>
          tripsApi.disable(this.companyId, trip.id)
        );
        await Promise.all(promises);
      } catch (error) {
        console.error("Ошибка при отключении рейсов:", error);
        throw error;
      }
    },

    async duplicateTrips(tripsList, options) {
      try {
        this.dataIsLoading = true;
        const promises = tripsList.map((trip) =>
          tripsApi.duplicate(this.companyId, trip.id, options)
        );
        await Promise.all(promises);
      } catch (error) {
        console.error("Ошибка при дублировании:", error);
        throw error;
      } finally {
        this.dataIsLoading = false;
      }
    },

    clearTrips() {
      this.trips = [];
      this.lastFetchParams = {};
      this.pagination = {
        page: 1,
        pages: 1,
        series: [],
        next: null,
        prev: null,
        count: 0,
        limit: 40,
        from: 0,
        to: 0,
      };
    },
  },
});
